"use client";

import type { FC, FormEvent } from "react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { toast } from "@/components/toast";
import { browserCmw } from "@/lib/ky/browser";
import { getApiErrorMessage } from "@/lib/ky/error-message";

export const NewsletterForm: FC = () => {
  const [email, setEmail] = useState("");

  const subscribe = useMutation({
    mutationFn: (address: string) => browserCmw.newsletter.subscribe({ email: address }),
    onSuccess: () => {
      setEmail("");
      toast.show("Thanks for subscribing! You'll now get CMW updates in your inbox.");
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error, "Something went wrong subscribing you. Please try again."));
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    subscribe.mutate(email.trim());
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-66 w-full items-center gap-8 rounded-medium bg-brand-blue/10 py-8 pl-24 pr-8 lg:h-72 lg:w-384"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Your email address"
        aria-label="Your email address"
        className="min-w-0 flex-1 bg-transparent text-14 leading-[1.4] text-neutral-black outline-none placeholder:text-neutral-black/50 lg:text-16 lg:leading-[1.7]"
      />
      <Button
        type="submit"
        variant="primary-blue"
        size="m"
        disabled={subscribe.isPending}
        className="h-50 w-58 shrink-0 px-16 py-12"
        aria-label="Subscribe"
      >
        <ArrowRightIcon className="size-26" aria-hidden />
      </Button>
    </form>
  );
};
